import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Box, Typography } from '@mui/material';

const DEFAULT_LENGTH = 6;

export interface OtpInputProps {
  value: string;
  onChange: (value: string) => void;
  length?: number;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
  autoFocus?: boolean;
  id?: string;
  onComplete?: (value: string) => void;
  onBlur?: () => void;
}

const OtpInput: React.FC<OtpInputProps> = ({
  value,
  onChange,
  length = DEFAULT_LENGTH,
  error = false,
  helperText,
  disabled = false,
  autoFocus = true,
  id = 'otp',
  onComplete,
  onBlur,
}) => {
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);
  const valueRef = useRef(value);
  const advancingRef = useRef(false);
  const digits = useMemo(
    () => Array.from({ length }, (_, index) => value[index] ?? ''),
    [length, value]
  );

  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  const focusInput = useCallback((index: number) => {
    const clamped = Math.max(0, Math.min(index, length - 1));
    inputRefs.current[clamped]?.focus();
    inputRefs.current[clamped]?.select();
  }, [length]);

  const moveFocus = useCallback((index: number) => {
    advancingRef.current = true;
    focusInput(index);
  }, [focusInput]);

  useEffect(() => {
    if (!autoFocus || disabled || value.length > 0) {
      return;
    }
    focusInput(0);
  }, [autoFocus, disabled, focusInput, value.length]);

  const emitValue = useCallback((nextValue: string) => {
    const sanitized = nextValue.replace(/\D/g, '').slice(0, length);
    valueRef.current = sanitized;
    onChange(sanitized);
    if (sanitized.length === length) {
      onComplete?.(sanitized);
    }
    return sanitized;
  }, [length, onChange, onComplete]);

  const handleChange = (index: number, rawValue: string) => {
    if (disabled) {
      return;
    }

    const current = valueRef.current;
    const cleaned = rawValue.replace(/\D/g, '');
    if (!cleaned) {
      emitValue(current.slice(0, index));
      return;
    }

    if (cleaned.length > 1) {
      const merged = `${current.slice(0, index)}${cleaned}`.slice(0, length);
      emitValue(merged);
      moveFocus(Math.min(merged.length, length - 1));
      return;
    }

    const nextValue =
      index >= current.length
        ? `${current}${cleaned}`
        : `${current.slice(0, index)}${cleaned}${current.slice(index + 1)}`;
    emitValue(nextValue.slice(0, length));
    moveFocus(Math.min(index + 1, length - 1));
  };

  const handleKeyDown = (index: number, event: React.KeyboardEvent<HTMLInputElement>) => {
    if (disabled) {
      return;
    }

    if (/^[0-9]$/.test(event.key)) {
      event.preventDefault();
      const current = valueRef.current;
      const nextValue =
        index >= current.length
          ? `${current}${event.key}`
          : `${current.slice(0, index)}${event.key}${current.slice(index + 1)}`;
      emitValue(nextValue.slice(0, length));
      moveFocus(Math.min(index + 1, length - 1));
      return;
    }

    if (event.key === 'Backspace') {
      event.preventDefault();
      const current = valueRef.current;
      if (current[index]) {
        emitValue(current.slice(0, index));
        return;
      }
      if (index > 0) {
        emitValue(current.slice(0, index - 1));
        moveFocus(index - 1);
      }
      return;
    }

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      focusInput(index - 1);
      return;
    }

    if (event.key === 'ArrowRight') {
      event.preventDefault();
      focusInput(Math.min(index + 1, valueRef.current.length));
      return;
    }

    if (event.key === 'Home') {
      event.preventDefault();
      focusInput(0);
      return;
    }

    if (event.key === 'End') {
      event.preventDefault();
      focusInput(Math.min(valueRef.current.length, length - 1));
    }
  };

  const handlePaste = (index: number, event: React.ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    if (disabled) {
      return;
    }
    const pasted = event.clipboardData.getData('text').replace(/\D/g, '');
    if (!pasted) {
      return;
    }
    const current = valueRef.current;
    const merged = `${current.slice(0, index)}${pasted}`.slice(0, length);
    emitValue(merged);
    moveFocus(Math.min(merged.length, length - 1));
  };

  const handleBoxFocus = (index: number) => {
    if (advancingRef.current) {
      advancingRef.current = false;
      return;
    }
    const firstEmpty = valueRef.current.length;
    if (firstEmpty < length && index > firstEmpty) {
      focusInput(firstEmpty);
    }
  };

  return (
    <Box sx={{ width: '100%', mt: 2, mb: 1 }}>
      <Box
        role="group"
        aria-label={`${length}-digit verification code`}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: { xs: 0.75, sm: 1 },
        }}
      >
        {digits.map((digit, index) => (
            <Box
              key={index}
              component="input"
              id={index === 0 ? id : `${id}-${index}`}
              type="text"
              inputMode="numeric"
              autoComplete={index === 0 ? 'one-time-code' : 'off'}
              pattern="[0-9]*"
              maxLength={length}
              disabled={disabled}
              value={digit}
              aria-label={`Digit ${index + 1} of ${length}`}
              aria-invalid={error}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => handleChange(index, event.target.value)}
              onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => handleKeyDown(index, event)}
              onPaste={(event: React.ClipboardEvent<HTMLInputElement>) => handlePaste(index, event)}
              onFocus={() => handleBoxFocus(index)}
              onBlur={() => {
                requestAnimationFrame(() => {
                  const stillInside = inputRefs.current.some(
                    (input) => input === document.activeElement
                  );
                  if (!stillInside) {
                    onBlur?.();
                  }
                });
              }}
              ref={(element: HTMLInputElement | null) => {
                inputRefs.current[index] = element;
              }}
              sx={{
                width: { xs: 40, sm: 48 },
                height: { xs: 48, sm: 56 },
                p: 0,
                border: '1.5px solid',
                borderColor: error
                  ? 'error.main'
                  : digit
                    ? 'primary.main'
                    : 'divider',
                borderRadius: 2,
                textAlign: 'center',
                fontSize: { xs: '1.25rem', sm: '1.5rem' },
                fontWeight: 600,
                fontFamily: 'inherit',
                letterSpacing: 0,
                color: 'text.primary',
                backgroundColor: disabled ? 'action.disabledBackground' : 'background.paper',
                caretColor: 'primary.main',
                outline: 'none',
                transition: 'border-color 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease',
                boxShadow: digit && !error ? '0 0 0 3px rgba(25, 118, 210, 0.08)' : 'none',
                '&:hover:not(:disabled)': {
                  borderColor: error ? 'error.main' : 'primary.light',
                },
                '&:focus': {
                  borderColor: error ? 'error.main' : 'primary.main',
                  boxShadow: error
                    ? '0 0 0 4px rgba(211, 47, 47, 0.16)'
                    : '0 0 0 4px rgba(25, 118, 210, 0.18)',
                },
                '&:disabled': {
                  cursor: 'not-allowed',
                  opacity: 0.7,
                },
              }}
            />
        ))}
      </Box>
      {helperText ? (
        <Typography
          variant="caption"
          color={error ? 'error' : 'text.secondary'}
          sx={{ display: 'block', textAlign: 'center', mt: 1.25, minHeight: 18 }}
        >
          {helperText}
        </Typography>
      ) : null}
    </Box>
  );
};

export default OtpInput;
